import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { getValidAuthHeadersOrThrow } from '../lib/auth';
import { fetchAllPaginated } from '../lib/fetchAllPaginated';
import {
  calculateProfitMetrics,
  linearRegressionForecast,
  getAllABCClassifications,
  type ProfitMetrics,
  type ForecastData,
} from '../utils/analyticsCalculations';
import { type TimeRange } from '../components/reports/TimeRangeSelector';
import { getDateRangeForPeriod } from '../utils/overviewCalculations';

export interface ProductWithDetails {
  id: string;
  name: string;
  revenue: number;
  profit: number;
  quantity: number;
  colorName?: string;
  colorHex?: string;
  sizeName?: string;
}

export interface EnterpriseAnalytics {
  profit: {
    grossProfit: number;
    grossMargin: number;
    netProfit: number;
    netMargin: number;
    costOfGoodsSold: number;
    trends: {
      grossProfit: { current: number; previous: number; change: number; changePercent: number };
      grossMargin: { current: number; previous: number; change: number; changePercent: number };
    };
    profitTrend: Array<{ date: string; value: number }>;
  };
  products: {
    topProducts: ProductWithDetails[];
    bottomProducts: ProductWithDetails[];
    abcAnalysis: {
      A: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
      B: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
      C: { products: Array<{ id: string; name: string; value: number; percentage: number }> };
    };
  };
  forecasting: {
    revenueForecast: ForecastData[];
  };
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  refetch: () => void;
}

export function useEnterpriseAnalytics(
  adminId: string | null,
  timeRange: TimeRange
): EnterpriseAnalytics {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [analytics, setAnalytics] = useState<Omit<EnterpriseAnalytics, 'loading' | 'error' | 'lastUpdated' | 'refetch'>>({
    profit: {
      grossProfit: 0, grossMargin: 0, netProfit: 0, netMargin: 0, costOfGoodsSold: 0,
      trends: {
        grossProfit: { current: 0, previous: 0, change: 0, changePercent: 0 },
        grossMargin: { current: 0, previous: 0, change: 0, changePercent: 0 },
      },
      profitTrend: [],
    },
    products: {
      topProducts: [], bottomProducts: [],
      abcAnalysis: { A: { products: [] }, B: { products: [] }, C: { products: [] } },
    },
    forecasting: { revenueForecast: [] },
  });

  const fetchAnalytics = useCallback(async () => {
    if (!adminId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { start: currentStart, end: currentEnd } = getDateRangeForPeriod(timeRange);

      const previousStart = new Date(currentStart);
      const previousEnd = new Date(currentEnd);
      const periodDays = Math.ceil((currentEnd.getTime() - currentStart.getTime()) / (1000 * 60 * 60 * 24));
      previousStart.setDate(previousStart.getDate() - periodDays);
      previousEnd.setDate(previousEnd.getDate() - periodDays);

      const startStr = currentStart.toISOString().split('T')[0];
      const endStr = currentEnd.toISOString().split('T')[0];
      const prevStartStr = previousStart.toISOString().split('T')[0];
      const prevEndStr = previousEnd.toISOString().split('T')[0];
      const analyticsUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/shopify-fetch-analytics`;

      const [currentAnalyticsRes, previousAnalyticsRes, currentInvoicesRes, previousInvoicesRes, allProductsRes, colorsRes, sizesRes] = await Promise.all([
        fetch(analyticsUrl, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ startDate: startStr, endDate: endStr }),
        }),
        fetch(analyticsUrl, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ startDate: prevStartStr, endDate: prevEndStr }),
        }),
        fetchAllPaginated((from, to) =>
          supabase
            .from('invoices')
            .select('id, total_amount, payment_status, created_at, invoice_items!inner(product_id, quantity, unit_price)')
            .eq('admin_id', adminId)
            .gte('created_at', currentStart.toISOString())
            .lte('created_at', currentEnd.toISOString())
            .range(from, to)
        ),
        fetchAllPaginated((from, to) =>
          supabase
            .from('invoices')
            .select('id, total_amount, payment_status, created_at, invoice_items!inner(product_id, quantity, unit_price)')
            .eq('admin_id', adminId)
            .gte('created_at', previousStart.toISOString())
            .lte('created_at', previousEnd.toISOString())
            .range(from, to)
        ),
        fetchAllPaginated((from, to) =>
          supabase
            .from('products')
            .select('id, name, sku, purchase_price, cost_price, sell_price, quantity, color_id, size_id')
            .eq('admin_id', adminId)
            .range(from, to)
        ),
        supabase
          .from('product_colors')
          .select('id, name, hex_code')
          .eq('admin_id', adminId),
        supabase
          .from('product_sizes')
          .select('id, name')
          .eq('admin_id', adminId),
      ]);

      const currentAnalytics = currentAnalyticsRes.ok ? await currentAnalyticsRes.json().catch(() => ({})) : {};
      const previousAnalytics = previousAnalyticsRes.ok ? await previousAnalyticsRes.json().catch(() => ({})) : {};
      const currentShopifyOrders = currentAnalytics.orders ?? [];
      const previousShopifyOrders = previousAnalytics.orders ?? [];
      const validShopifyOrders = currentShopifyOrders;
      const previousValidShopifyOrders = previousShopifyOrders;

      const currentInvoicesRaw = currentInvoicesRes.data || [];
      const previousInvoicesRaw = previousInvoicesRes.data || [];
      const currentInvoices = currentInvoicesRaw.filter((inv: any) => (inv.payment_status ?? '').toLowerCase() !== 'cancelled');
      const previousInvoices = previousInvoicesRaw.filter((inv: any) => (inv.payment_status ?? '').toLowerCase() !== 'cancelled');
      const allProducts = allProductsRes.data || [];

      // Color and size lookup maps
      const colorMap = new Map<string, { name: string; hex: string }>();
      (colorsRes.data || []).forEach(c => colorMap.set(c.id, { name: c.name, hex: c.hex_code || '' }));

      const sizeMap = new Map<string, string>();
      (sizesRes.data || []).forEach(s => sizeMap.set(s.id, s.name));

      // Product lookup maps
      const productCostMap = new Map<string, number>();
      const productNameMap = new Map<string, string>();
      const productColorMap = new Map<string, { name: string; hex: string }>();
      const productSizeMap = new Map<string, string>();
      const skuToIdMap = new Map<string, string>();

      allProducts.forEach(p => {
        const cost = p.purchase_price || p.cost_price || 0;
        productCostMap.set(p.id, cost);
        productNameMap.set(p.id, p.name);
        if (p.sku) skuToIdMap.set(p.sku.toLowerCase(), p.id);
        if (p.color_id) {
          const color = colorMap.get(p.color_id);
          if (color) productColorMap.set(p.id, color);
        }
        if (p.size_id) {
          const size = sizeMap.get(p.size_id);
          if (size) productSizeMap.set(p.id, size);
        }
      });

      // --- COGS and product performance from invoices ---
      let invoiceCOGS = 0;
      const productRevenueMap = new Map<string, {
        revenue: number; profit: number; quantity: number; name: string;
        colorName?: string; colorHex?: string; sizeName?: string;
      }>();

      currentInvoices.forEach(inv => {
        if (inv.invoice_items && Array.isArray(inv.invoice_items)) {
          inv.invoice_items.forEach((item: any) => {
            const cost = productCostMap.get(item.product_id) || 0;
            const qty = item.quantity || 0;
            const revenue = (item.unit_price || 0) * qty;
            invoiceCOGS += cost * qty;

            const color = productColorMap.get(item.product_id);
            const size = productSizeMap.get(item.product_id);

            const existing = productRevenueMap.get(item.product_id) || {
              revenue: 0, profit: 0, quantity: 0,
              name: productNameMap.get(item.product_id) || 'Unknown',
              colorName: color?.name, colorHex: color?.hex, sizeName: size,
            };
            existing.revenue += revenue;
            existing.profit += revenue - (cost * qty);
            existing.quantity += qty;
            productRevenueMap.set(item.product_id, existing);
          });
        }
      });

      // --- COGS and product performance from Shopify line_items (from API) ---
      let shopifyCOGS = 0;
      validShopifyOrders.forEach((order: any) => {
        for (const edge of order.lineItems?.edges ?? []) {
          const item = edge.node;
          const qty = item?.quantity ?? 0;
          const price = parseFloat(item?.originalUnitPriceSet?.shopMoney?.amount ?? '0');
          const revenue = price * qty;
          const sku = item?.sku ?? item?.variant?.sku;
          const title = item?.title ?? item?.product?.title ?? 'Unknown';

          let productId: string | undefined;
          if (sku) productId = skuToIdMap.get(String(sku).toLowerCase());
          const cost = productId ? (productCostMap.get(productId) || 0) : 0;
          shopifyCOGS += cost * qty;

          const key = productId || `shopify_${title}`;
          const color = productId ? productColorMap.get(productId) : undefined;
          const size = productId ? productSizeMap.get(productId) : undefined;

          const existing = productRevenueMap.get(key) || {
            revenue: 0, profit: 0, quantity: 0,
            name: (productId ? productNameMap.get(productId) : null) || title,
            colorName: color?.name, colorHex: color?.hex, sizeName: size,
          };
          existing.revenue += revenue;
          existing.profit += revenue - (cost * qty);
          existing.quantity += qty;
          productRevenueMap.set(key, existing);
        }
      });

      const totalCOGS = invoiceCOGS + shopifyCOGS;

      // --- Revenue ---
      const currentInvoiceRevenue = currentInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
      const currentShopifyRevenue = currentAnalytics.totalRevenue ?? 0;
      const currentRevenue = currentInvoiceRevenue + currentShopifyRevenue;

      // --- Previous period COGS ---
      let previousCOGS = 0;
      previousInvoices.forEach(inv => {
        if (inv.invoice_items && Array.isArray(inv.invoice_items)) {
          inv.invoice_items.forEach((item: any) => {
            const cost = productCostMap.get(item.product_id) || 0;
            previousCOGS += cost * (item.quantity || 0);
          });
        }
      });
      previousValidShopifyOrders.forEach((order: any) => {
        for (const edge of order.lineItems?.edges ?? []) {
          const item = edge.node;
          const qty = item?.quantity ?? 0;
          const sku = item?.sku ?? item?.variant?.sku;
          let productId: string | undefined;
          if (sku) productId = skuToIdMap.get(String(sku).toLowerCase());
          const cost = productId ? (productCostMap.get(productId) || 0) : 0;
          previousCOGS += cost * qty;
        }
      });

      const previousInvoiceRevenue = previousInvoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
      const previousShopifyRevenue = previousAnalytics.totalRevenue ?? 0;
      const previousRevenue = previousInvoiceRevenue + previousShopifyRevenue;

      const profitMetrics = calculateProfitMetrics(currentRevenue, totalCOGS, 0);
      const previousProfitMetrics = calculateProfitMetrics(previousRevenue, previousCOGS, 0);

      // --- Profit trend from daily revenue - daily COGS ---
      const dailyProfitMap = new Map<string, number>();
      const addToDailyProfit = (dateStr: string, amount: number) => {
        const key = new Date(dateStr).toISOString().split('T')[0];
        dailyProfitMap.set(key, (dailyProfitMap.get(key) || 0) + amount);
      };

      currentInvoices.forEach(inv => {
        let invCost = 0;
        if (inv.invoice_items && Array.isArray(inv.invoice_items)) {
          inv.invoice_items.forEach((item: any) => {
            invCost += (productCostMap.get(item.product_id) || 0) * (item.quantity || 0);
          });
        }
        addToDailyProfit(inv.created_at, (inv.total_amount || 0) - invCost);
      });
      validShopifyOrders.forEach((order: any) => {
        let orderCost = 0;
        for (const edge of order.lineItems?.edges ?? []) {
          const item = edge.node;
          const qty = item?.quantity ?? 0;
          const sku = item?.sku ?? item?.variant?.sku;
          let productId: string | undefined;
          if (sku) productId = skuToIdMap.get(String(sku).toLowerCase());
          orderCost += (productId ? (productCostMap.get(productId) || 0) : 0) * qty;
        }
        const totalPrice = parseFloat(order.totalPriceSet?.shopMoney?.amount ?? '0');
        const totalRefunded = parseFloat(order.totalRefundedSet?.shopMoney?.amount ?? '0');
        const orderRevenue = Math.max(0, totalPrice - totalRefunded);
        const dateStr = order.createdAt?.split?.('T')[0] ?? order.order_date;
        addToDailyProfit(dateStr, orderRevenue - orderCost);
      });

      const profitTrend = Array.from(dailyProfitMap.entries())
        .map(([date, value]) => ({ date, value }))
        .sort((a, b) => a.date.localeCompare(b.date));

      // --- Product performance with color/size ---
      const productPerformance: ProductWithDetails[] = Array.from(productRevenueMap.entries())
        .map(([id, data]) => ({ id, ...data }))
        .filter(p => p.revenue > 0);

      const topProducts = [...productPerformance]
        .sort((a, b) => b.revenue - a.revenue)
        .slice(0, 10);

      const bottomProducts = [...productPerformance]
        .sort((a, b) => a.revenue - b.revenue)
        .slice(0, 10);

      // --- ABC Analysis ---
      const productsForABC = productPerformance.map(p => ({
        id: p.id,
        name: p.name,
        value: p.revenue,
      }));
      const abcAnalysis = productsForABC.length > 0
        ? getAllABCClassifications(productsForABC)
        : { A: { category: 'A' as const, products: [] }, B: { category: 'B' as const, products: [] }, C: { category: 'C' as const, products: [] } };

      // --- Forecasting with 90-day history (Shopify from API, invoices from Supabase) ---
      const forecastStart = new Date();
      forecastStart.setDate(forecastStart.getDate() - 90);
      const forecastEnd = new Date();
      const forecastStartStr = forecastStart.toISOString().split('T')[0];
      const forecastEndStr = forecastEnd.toISOString().split('T')[0];

      const [forecastAnalyticsRes, forecastInvoicesRes] = await Promise.all([
        fetch(analyticsUrl, {
          method: 'POST',
          headers: await getValidAuthHeadersOrThrow(),
          body: JSON.stringify({ startDate: forecastStartStr, endDate: forecastEndStr }),
        }),
        fetchAllPaginated((from, to) =>
          supabase
            .from('invoices')
            .select('total_amount, payment_status, created_at')
            .eq('admin_id', adminId)
            .gte('created_at', forecastStart.toISOString())
            .range(from, to)
        ),
      ]);

      const forecastAnalytics = forecastAnalyticsRes.ok ? await forecastAnalyticsRes.json().catch(() => ({})) : {};
      const forecastOrders = forecastAnalytics.orders ?? [];

      const allRevenuePoints: Array<{ date: Date; value: number }> = [];
      (forecastInvoicesRes.data || [])
        .filter((inv: any) => (inv.payment_status ?? '').toLowerCase() !== 'cancelled')
        .forEach((inv: any) => {
          allRevenuePoints.push({ date: new Date(inv.created_at), value: inv.total_amount || 0 });
        });
      forecastOrders.forEach((ord: any) => {
        const totalPrice = parseFloat(ord.totalPriceSet?.shopMoney?.amount ?? '0');
        const totalRefunded = parseFloat(ord.totalRefundedSet?.shopMoney?.amount ?? '0');
        allRevenuePoints.push({ date: new Date(ord.createdAt), value: Math.max(0, totalPrice - totalRefunded) });
      });
      allRevenuePoints.sort((a, b) => a.date.getTime() - b.date.getTime());

      const revenueForecast = linearRegressionForecast(allRevenuePoints, 30);

      setAnalytics({
        profit: {
          ...profitMetrics,
          trends: {
            grossProfit: {
              current: profitMetrics.grossProfit,
              previous: previousProfitMetrics.grossProfit,
              change: profitMetrics.grossProfit - previousProfitMetrics.grossProfit,
              changePercent: previousProfitMetrics.grossProfit > 0
                ? ((profitMetrics.grossProfit - previousProfitMetrics.grossProfit) / previousProfitMetrics.grossProfit) * 100
                : 0,
            },
            grossMargin: {
              current: profitMetrics.grossMargin,
              previous: previousProfitMetrics.grossMargin,
              change: profitMetrics.grossMargin - previousProfitMetrics.grossMargin,
              changePercent: previousProfitMetrics.grossMargin > 0
                ? ((profitMetrics.grossMargin - previousProfitMetrics.grossMargin) / previousProfitMetrics.grossMargin) * 100
                : 0,
            },
          },
          profitTrend,
        },
        products: {
          topProducts,
          bottomProducts,
          abcAnalysis,
        },
        forecasting: { revenueForecast },
      });

      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching enterprise analytics:', err);
      setError(err instanceof Error ? err.message : 'Failed to load analytics');
    } finally {
      setLoading(false);
    }
  }, [adminId, timeRange]);

  useEffect(() => {
    fetchAnalytics();
  }, [fetchAnalytics]);

  return {
    ...analytics,
    loading,
    error,
    lastUpdated,
    refetch: fetchAnalytics,
  };
}
